import countries from 'constants/countries';
import countriesFi from 'constants/countries-fi';
import countriesSv from 'constants/countries-sv';
import countriesDe from 'constants/countries-de';
import * as Sentry from '@sentry/browser';
import * as DOMPurify from 'dompurify';
import { SanitizedHTMLObject } from 'types/models/utilities';
import { ALPHABETIC_WITH_SPACE_REGEX, MAX_NAME_LENGTH } from 'constants/utils';

export const delay = async (timeout = 200, data = null): Promise<any> => {
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve(data);
    }, timeout);
  });
};

/**
 * Get countries
 */
export function getCountries(
  lang: string,
): Array<{ value: string; name: string }> {
  switch (lang) {
    case 'fi':
      return countriesFi;
    case 'de':
      return countriesDe;
    case 'sv':
      return countriesSv;
  }
  return countries;
}

/**
 * Sort countries
 */
export function sortCountries(
  countries: Array<{ value: string; name: string }>,
): Array<{ value: string; name: string }> {
  return countries.sort((a, b) => a.name.localeCompare(b.name));
}

export const currencyFormatter = (currency: string): any => {
  return new Intl.NumberFormat('it-IT', {
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export function padNumber(number: any): any {
  if (number <= 999) {
    number = `00${number}`.slice(-4);
  }
  return number;
}

export const toArray = (data: any): any[] => {
  if (data) {
    return Object.values(data).map((value: any) => {
      return value;
    });
  }

  return [];
};

export const parseJsonData = (data: string | null): any => {
  if (!data) return {};
  try {
    return JSON.parse(data);
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setLevel(Sentry.Severity.Fatal);
      Sentry.captureException(error);
    });
  }
  return {};
};

/**
 * Check if caravan club membership number is in correct format.
 * @param number the number to check
 */
export const isValidMembershipNumber = (number: string): boolean => {
  // Number is not mandatory, so return true if empty
  if (!number) return true;
  // Otherwise check against regex, should be like 123456-1
  const pattern = /^\d+-\d$/;
  return pattern.test(number);
};

/**
 * Removes spaces from given string.
 * @param str the string to handle
 * @returns string without spaces
 */
export const removeSpacesFromString = (str: string): string => {
  if (!str) return '';
  return str.replace(/\s/g, '');
};

/**
 * Helper to test that input value contains
 * only digits. Negatives can be allowed by
 * parameter.
 * @param value value to be tested
 * @param allowNegatives boolean
 * @returns true if all digits else false
 */
export const hasOnlyDigits = (value: any, allowNegatives = false): boolean => {
  const regex = allowNegatives ? /^-?\d+$/ : /^\d+$/;
  return regex.test(value);
};

/**
 * Creates a sanitized dangerouslySetInnerHTML object from the provided HTML content.
 * @param {string} content - The HTML content to sanitize.
 * @param {boolean} linksInNewWindow - Flag to allow/disallow links to open in a new window.
 * @returns {SanitizedHTMLObject} - The sanitized dangerouslySetInnerHTML object.
 */
export const createSanitizedHTMLObject = (
  content: string,
  linksInNewWindow: boolean = false,
): SanitizedHTMLObject => {
  if (linksInNewWindow) {
    DOMPurify.addHook('afterSanitizeAttributes', (node: any) => {
      if ('target' in node && node.getAttribute('target') !== '_blank') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }
  return { __html: DOMPurify.sanitize(content) };
};

/**
 * Validates a name string according to specified criteria.
 *
 * @param {string} value - The name string to be validated.
 * @returns {boolean} - True if the name is valid; otherwise, false.
 *
 * @description
 * This function validates a name string based on the following criteria:
 * 1. Must not be empty or null.
 * 2. Must not exceed the maximum allowed length (MAX_NAME_LENGTH).
 * 3. Must consist of alphabetic characters and optional spaces.
 */
export const validateName = (value: string): boolean => {
  if (!value) return false;
  if (value.length > MAX_NAME_LENGTH) return false;
  if (value.trim().length > 0) {
    return ALPHABETIC_WITH_SPACE_REGEX.test(value);
  }
  return true;
};
